import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  Search,
  Globe,
  Zap,
  CreditCard,
  Phone,
  Wifi,
  Gift,
  Loader2,
  X,
  Check,
  MapPin
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAppState } from '../context/AppStateContext';
import PayBillSlider from '../components/shop/PayBillSlider';

// Timezone to country mapping for fallback detection
const TIMEZONE_TO_COUNTRY = {
  'Africa/Lagos': 'Nigeria',
  'Africa/Accra': 'Ghana',
  'Africa/Nairobi': 'Kenya',
  'Africa/Johannesburg': 'South Africa',
  'Africa/Cairo': 'Egypt',
  'Africa/Casablanca': 'Morocco',
  'Africa/Douala': 'Cameroon',
  'Africa/Kigali': 'Rwanda',
  'Africa/Dar_es_Salaam': 'Tanzania',
  'Africa/Kampala': 'Uganda',
  'Africa/Addis_Ababa': 'Ethiopia',
  'Africa/Dakar': 'Senegal',
  'America/New_York': 'United States',
  'America/Los_Angeles': 'United States',
  'America/Chicago': 'United States',
  'America/Denver': 'United States',
  'America/Toronto': 'Canada',
  'America/Vancouver': 'Canada',
  'America/Mexico_City': 'Mexico',
  'America/Sao_Paulo': 'Brazil',
  'Europe/London': 'United Kingdom',
  'Europe/Paris': 'France',
  'Europe/Berlin': 'Germany',
  'Europe/Madrid': 'Spain',
  'Europe/Rome': 'Italy',
  'Europe/Amsterdam': 'Netherlands',
  'Europe/Brussels': 'Belgium',
  'Europe/Vienna': 'Austria',
  'Europe/Lisbon': 'Portugal',
  'Europe/Dublin': 'Ireland',
  'Europe/Athens': 'Greece',
  'Europe/Warsaw': 'Poland',
  'Asia/Dubai': 'United Arab Emirates',
  'Asia/Riyadh': 'Saudi Arabia',
  'Asia/Singapore': 'Singapore',
  'Asia/Kuala_Lumpur': 'Malaysia',
  'Asia/Jakarta': 'Indonesia',
  'Asia/Manila': 'Philippines',
  'Asia/Bangkok': 'Thailand',
  'Asia/Ho_Chi_Minh': 'Vietnam',
  'Asia/Tokyo': 'Japan',
  'Asia/Seoul': 'South Korea',
  'Asia/Shanghai': 'China',
  'Asia/Kolkata': 'India',
  'Australia/Sydney': 'Australia',
  'Australia/Melbourne': 'Australia',
  'Pacific/Auckland': 'New Zealand',
};

// Get country from browser timezone
const getCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('[BonndleBills] Browser timezone:', timezone);
    return TIMEZONE_TO_COUNTRY[timezone] || null;
  } catch (error) {
    console.error('[BonndleBills] Timezone detection failed:', error);
    return null;
  }
};

// IP Geolocation API helper with timezone fallback
const detectUserCountry = async () => {
  // Try IP-based geolocation first
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.country_name) {
        console.log('[BonndleBills] IP geolocation detected:', data.country_name);
        return data.country_name;
      }
    }
  } catch (error) {
    console.log('[BonndleBills] ipapi.co failed, trying fallback...');
  }

  try {
    const response = await fetch('http://ip-api.com/json/');
    if (response.ok) {
      const data = await response.json();
      if (data.country) {
        console.log('[BonndleBills] IP fallback detected:', data.country);
        return data.country;
      }
    }
  } catch (error) {
    console.log('[BonndleBills] ip-api.com failed, trying timezone...');
  }

  // Fallback to timezone-based detection
  const timezoneCountry = getCountryFromTimezone();
  if (timezoneCountry) {
    console.log('[BonndleBills] Timezone fallback detected:', timezoneCountry);
    return timezoneCountry;
  }

  console.log('[BonndleBills] All geolocation methods failed');
  return null;
};

// Use production Boldscale API for Bonndle Bills page only
const PROD_API_URL = 'https://boldscaleservices.com';

// Bill Lion merchant info (hardcoded - no Wix API needed for this page)
const BILL_LION_MERCHANT = {
  name: 'Bill Lion',
  logo: 'https://static.wixstatic.com/media/b1e89f_39e6ae91aa474f17bc96a7d1b83e1383~mv2.png',
};

// Production API helpers
const prodApiFetch = async (endpoint) => {
  const response = await fetch(`${PROD_API_URL}${endpoint}`);
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
};

const extractArray = (response) => {
  if (Array.isArray(response)) return response;
  if (response?.data && Array.isArray(response.data)) return response.data;
  if (response?.providers && Array.isArray(response.providers)) return response.providers;
  if (response?.services && Array.isArray(response.services)) return response.services;
  return [];
};

// Production Services API
const prodServicesApi = {
  async getAll(limit = 100) {
    const response = await prodApiFetch(`/services?page=1&limit=${limit}`);
    return extractArray(response);
  }
};

// Production Providers API (no vtuProvider filter on prod - data uses different provider values)
const prodProvidersApi = {
  async getAllProviders(limit = 1000) {
    const response = await prodApiFetch(`/providers?page=1&limit=${limit}`);
    return extractArray(response);
  }
};

// Service icons mapping
const SERVICE_ICONS = {
  'Bill Payment': Zap,
  'Mobile Top Up': Phone,
  'Mobile Data': Wifi,
  'Mobile Pin / Voucher': Gift,
};

// Get service icon component
const getServiceIcon = (serviceName) => {
  return SERVICE_ICONS[serviceName] || CreditCard;
};

// Country flag emoji helper
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Comprehensive country code mapping (ISO 3166-1 alpha-2)
const COUNTRY_CODES = {
  // Africa
  'Nigeria': 'NG',
  'Ghana': 'GH',
  'Kenya': 'KE',
  'South Africa': 'ZA',
  'Egypt': 'EG',
  'Morocco': 'MA',
  'Cameroon': 'CM',
  'Rwanda': 'RW',
  'Tanzania': 'TZ',
  'Uganda': 'UG',
  'Ethiopia': 'ET',
  'Senegal': 'SN',
  'Ivory Coast': 'CI',
  'Côte d\'Ivoire': 'CI',
  'Cote d\'Ivoire': 'CI',
  'Algeria': 'DZ',
  'Tunisia': 'TN',
  'Libya': 'LY',
  'Sudan': 'SD',
  'Angola': 'AO',
  'Mozambique': 'MZ',
  'Zimbabwe': 'ZW',
  'Zambia': 'ZM',
  'Botswana': 'BW',
  'Namibia': 'NA',
  'Mali': 'ML',
  'Burkina Faso': 'BF',
  'Niger': 'NE',
  'Benin': 'BJ',
  'Togo': 'TG',
  'Sierra Leone': 'SL',
  'Liberia': 'LR',
  'Guinea': 'GN',
  'Gambia': 'GM',
  'The Gambia': 'GM',
  'Mauritius': 'MU',
  'Madagascar': 'MG',
  'Malawi': 'MW',
  'Congo': 'CG',
  'Democratic Republic of Congo': 'CD',
  'DRC': 'CD',
  'Gabon': 'GA',
  'Equatorial Guinea': 'GQ',
  'Chad': 'TD',
  'Central African Republic': 'CF',
  'Somalia': 'SO',
  'Eritrea': 'ER',
  'Djibouti': 'DJ',
  'Burundi': 'BI',
  'Lesotho': 'LS',
  'Eswatini': 'SZ',
  'Swaziland': 'SZ',
  'Mauritania': 'MR',
  'Cape Verde': 'CV',
  'Cabo Verde': 'CV',
  'Comoros': 'KM',
  'Seychelles': 'SC',
  'South Sudan': 'SS',

  // Americas
  'United States': 'US',
  'USA': 'US',
  'Canada': 'CA',
  'Mexico': 'MX',
  'Brazil': 'BR',
  'Argentina': 'AR',
  'Colombia': 'CO',
  'Peru': 'PE',
  'Venezuela': 'VE',
  'Chile': 'CL',
  'Ecuador': 'EC',
  'Bolivia': 'BO',
  'Paraguay': 'PY',
  'Uruguay': 'UY',
  'Costa Rica': 'CR',
  'Panama': 'PA',
  'Guatemala': 'GT',
  'Honduras': 'HN',
  'El Salvador': 'SV',
  'Nicaragua': 'NI',
  'Cuba': 'CU',
  'Dominican Republic': 'DO',
  'Haiti': 'HT',
  'Jamaica': 'JM',
  'Trinidad and Tobago': 'TT',
  'Puerto Rico': 'PR',
  'Bahamas': 'BS',
  'Barbados': 'BB',
  'Guyana': 'GY',
  'Suriname': 'SR',
  'Belize': 'BZ',

  // Europe
  'United Kingdom': 'GB',
  'UK': 'GB',
  'Germany': 'DE',
  'France': 'FR',
  'Italy': 'IT',
  'Spain': 'ES',
  'Netherlands': 'NL',
  'Belgium': 'BE',
  'Austria': 'AT',
  'Switzerland': 'CH',
  'Portugal': 'PT',
  'Ireland': 'IE',
  'Greece': 'GR',
  'Poland': 'PL',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Denmark': 'DK',
  'Finland': 'FI',
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Romania': 'RO',
  'Hungary': 'HU',
  'Ukraine': 'UA',
  'Russia': 'RU',
  'Turkey': 'TR',
  'Bulgaria': 'BG',
  'Serbia': 'RS',
  'Croatia': 'HR',
  'Slovakia': 'SK',
  'Slovenia': 'SI',
  'Lithuania': 'LT',
  'Latvia': 'LV',
  'Estonia': 'EE',
  'Cyprus': 'CY',
  'Luxembourg': 'LU',
  'Malta': 'MT',
  'Iceland': 'IS',
  'Albania': 'AL',
  'North Macedonia': 'MK',
  'Macedonia': 'MK',
  'Bosnia and Herzegovina': 'BA',
  'Montenegro': 'ME',
  'Kosovo': 'XK',
  'Moldova': 'MD',
  'Belarus': 'BY',
  'Georgia': 'GE',
  'Armenia': 'AM',
  'Azerbaijan': 'AZ',

  // Middle East
  'Saudi Arabia': 'SA',
  'United Arab Emirates': 'AE',
  'UAE': 'AE',
  'Qatar': 'QA',
  'Kuwait': 'KW',
  'Bahrain': 'BH',
  'Oman': 'OM',
  'Jordan': 'JO',
  'Lebanon': 'LB',
  'Israel': 'IL',
  'Palestine': 'PS',
  'Iraq': 'IQ',
  'Iran': 'IR',
  'Syria': 'SY',
  'Yemen': 'YE',

  // Asia
  'India': 'IN',
  'China': 'CN',
  'Japan': 'JP',
  'South Korea': 'KR',
  'Korea': 'KR',
  'Indonesia': 'ID',
  'Philippines': 'PH',
  'Vietnam': 'VN',
  'Thailand': 'TH',
  'Malaysia': 'MY',
  'Singapore': 'SG',
  'Myanmar': 'MM',
  'Burma': 'MM',
  'Cambodia': 'KH',
  'Laos': 'LA',
  'Bangladesh': 'BD',
  'Pakistan': 'PK',
  'Sri Lanka': 'LK',
  'Nepal': 'NP',
  'Afghanistan': 'AF',
  'Mongolia': 'MN',
  'Kazakhstan': 'KZ',
  'Uzbekistan': 'UZ',
  'Turkmenistan': 'TM',
  'Kyrgyzstan': 'KG',
  'Tajikistan': 'TJ',
  'Taiwan': 'TW',
  'Hong Kong': 'HK',
  'Macau': 'MO',
  'Maldives': 'MV',
  'Brunei': 'BN',
  'Bhutan': 'BT',
  'Timor-Leste': 'TL',
  'East Timor': 'TL',

  // Oceania
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Fiji': 'FJ',
  'Papua New Guinea': 'PG',
  'Samoa': 'WS',
  'Tonga': 'TO',
  'Vanuatu': 'VU',
  'Solomon Islands': 'SB',
};

const getCountryCode = (countryName) => {
  if (!countryName) return null;

  // Direct lookup
  if (COUNTRY_CODES[countryName]) {
    return COUNTRY_CODES[countryName];
  }

  // Case-insensitive lookup
  const lowerName = countryName.toLowerCase();
  for (const [country, code] of Object.entries(COUNTRY_CODES)) {
    if (country.toLowerCase() === lowerName) {
      return code;
    }
  }

  // Partial match (for variations like "The Gambia" vs "Gambia")
  for (const [country, code] of Object.entries(COUNTRY_CODES)) {
    if (country.toLowerCase().includes(lowerName) || lowerName.includes(country.toLowerCase())) {
      return code;
    }
  }

  return null; // Return null instead of guessing
};

export default function BonndleBillsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, isGuestMode } = useAuth();
  const { openPayBillSlider } = useAppState();

  // Merchant info (hardcoded for Bill Lion)
  const merchantName = BILL_LION_MERCHANT.name;
  const merchantLogo = BILL_LION_MERCHANT.logo;

  // Cashback percent from deal (read from URL params or localStorage)
  const [cashbackPercent, setCashbackPercent] = useState(null);

  // Read cashback percent from URL params or localStorage on mount
  useEffect(() => {
    // Try URL params first
    const params = new URLSearchParams(location.search);
    let percent = params.get('cashbackPercent');

    // Fallback to localStorage (injected by Flutter webview)
    if (!percent) {
      percent = localStorage.getItem('bonndle:cashback_percent');
    }

    if (percent) {
      const parsed = parseFloat(percent);
      if (!isNaN(parsed) && parsed > 0) {
        console.log('[BonndleBills] Cashback percent:', parsed);
        setCashbackPercent(parsed);
      }
    }
  }, [location.search]);

  // Data states
  const [services, setServices] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [countries, setCountries] = useState([]);

  // Loading states
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [detectingLocation, setDetectingLocation] = useState(true);

  // Selection states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);

  // Search states
  const [countrySearch, setCountrySearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');

  // Dropdown states
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef(null);

  // PayBillSlider state
  const [showPayBillSlider, setShowPayBillSlider] = useState(false);

  // Detect user's country on mount
  useEffect(() => {
    const detectLocation = async () => {
      setDetectingLocation(true);
      try {
        const detectedCountryName = await detectUserCountry();
        if (detectedCountryName) {
          setDetectedCountry(detectedCountryName);
          console.log('[BonndleBills] Auto-detected country:', detectedCountryName);
        }
      } catch (error) {
        console.error('[BonndleBills] Location detection failed:', error);
      } finally {
        setDetectingLocation(false);
      }
    };
    detectLocation();
  }, []);

  // Auto-select country when detected and providers are loaded
  useEffect(() => {
    if (detectedCountry && countries.length > 0 && !selectedCountry) {
      // Find matching country in available countries
      const matchedCountry = countries.find(c =>
        c.toLowerCase() === detectedCountry.toLowerCase() ||
        c.toLowerCase().includes(detectedCountry.toLowerCase()) ||
        detectedCountry.toLowerCase().includes(c.toLowerCase())
      );

      if (matchedCountry) {
        console.log('[BonndleBills] Auto-selecting detected country:', matchedCountry);
        setSelectedCountry(matchedCountry);
      }
    }
  }, [detectedCountry, countries, selectedCountry]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch services on mount (using production API)
  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        const data = await prodServicesApi.getAll();
        console.log('[BonndleBills] Services fetched from PROD:', data);
        setServices(data);
      } catch (error) {
        console.error('[BonndleBills] Failed to fetch services:', error);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  // Fetch all providers on mount (using production API)
  useEffect(() => {
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        // Fetch all providers from production
        const data = await prodProvidersApi.getAllProviders(1000);
        console.log('[BonndleBills] All providers fetched from PROD:', data.length);
        setAllProviders(data);

        // Extract unique countries
        const uniqueCountries = [...new Set(data.map(p => p.country).filter(Boolean))].sort();
        setCountries(uniqueCountries);
        console.log('[BonndleBills] Countries available:', uniqueCountries.length);
      } catch (error) {
        console.error('[BonndleBills] Failed to fetch providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, []);

  // Filter providers by selected country and service
  const filteredProviders = useMemo(() => {
    if (!selectedCountry) return [];

    let filtered = allProviders.filter(p => p.country === selectedCountry);

    // If service is selected, filter by service_uuid
    if (selectedService) {
      filtered = filtered.filter(p => p.service_uuid === selectedService.service_uuid);
    }

    // Apply search filter
    if (providerSearch) {
      const search = providerSearch.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(search));
    }

    return filtered;
  }, [allProviders, selectedCountry, selectedService, providerSearch]);

  // Get services available for selected country
  const availableServices = useMemo(() => {
    if (!selectedCountry) return [];

    // Get unique service UUIDs for providers in this country
    const serviceUuids = new Set(
      allProviders
        .filter(p => p.country === selectedCountry)
        .map(p => p.service_uuid)
    );

    // Return services that have providers in this country
    return services.filter(s => serviceUuids.has(s.service_uuid));
  }, [allProviders, selectedCountry, services]);

  // Filter countries by search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const search = countrySearch.toLowerCase();
    return countries.filter(c => c.toLowerCase().includes(search));
  }, [countries, countrySearch]);

  // Handle provider selection - open PayBillSlider
  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setShowPayBillSlider(true);
  };

  // Close PayBillSlider
  const handleClosePayBillSlider = () => {
    setShowPayBillSlider(false);
    setSelectedProvider(null);
  };

  // Handle back navigation
  const handleBack = () => {
    if (selectedService) {
      setSelectedService(null);
      setProviderSearch('');
    } else if (selectedCountry) {
      setSelectedCountry(null);
      setProviderSearch('');
    } else {
      navigate(-1);
    }
  };


  // Loading state
  const isLoading = loadingServices || loadingProviders;

  // Handle country change from dropdown
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedService(null);
    setProviderSearch('');
    setShowCountryDropdown(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          {/* Merchant Logo + Name */}
          <div className="flex items-center gap-2 flex-1">
            {merchantLogo ? (
              <img
                src={merchantLogo}
                alt={merchantName}
                className="w-8 h-8 rounded-lg object-contain bg-gray-50"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-8 h-8 bg-indigo-100 rounded-lg items-center justify-center ${merchantLogo ? 'hidden' : 'flex'}`}
            >
              <span className="text-indigo-600 font-bold text-lg">
                {merchantName.charAt(0)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">{merchantName}</h1>
              {cashbackPercent && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  {cashbackPercent}% cashback
                </span>
              )}
            </div>
          </div>

          {/* Country Flag Dropdown - show when country is selected */}
          {selectedCountry && (
            <div className="relative" ref={countryDropdownRef}>
              <button
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <span className="text-xl">{getCountryFlag(getCountryCode(selectedCountry))}</span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Country Dropdown */}
              {showCountryDropdown && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  {/* Search */}
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  {/* Country List */}
                  <div className="max-h-64 overflow-y-auto">
                    {filteredCountries.map((country) => (
                      <button
                        key={country}
                        onClick={() => handleCountryChange(country)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                          country === selectedCountry ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <span className="text-xl">{getCountryFlag(getCountryCode(country))}</span>
                        <span className="flex-1 text-sm font-medium text-gray-900">{country}</span>
                        {country === selectedCountry && (
                          <Check className="w-4 h-4 text-indigo-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {isLoading || detectingLocation ? (
          /* Loading State */
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
            <p className="text-gray-500 text-sm">
              {detectingLocation ? 'Detecting your location...' : 'Loading services...'}
            </p>
            {detectingLocation && (
              <p className="text-gray-400 text-xs mt-2">This will only take a moment</p>
            )}
          </div>
        ) : !selectedCountry ? (
          /* Step 1: Country Selection (fallback if auto-detect fails) */
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Select Your Country</h2>
              <p className="text-gray-500 text-sm">Choose your country to see available services</p>
            </div>

            {/* Country Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Country List */}
            <div className="grid grid-cols-1 gap-2">
              {filteredCountries.map((country) => (
                <button
                  key={country}
                  onClick={() => setSelectedCountry(country)}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 transition-all text-left"
                >
                  <span className="text-2xl">{getCountryFlag(getCountryCode(country))}</span>
                  <span className="flex-1 font-medium text-gray-900">{country}</span>
                  <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90" />
                </button>
              ))}
            </div>

            {filteredCountries.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No countries found matching "{countrySearch}"
              </div>
            )}
          </div>
        ) : !selectedService ? (
          /* Step 2: Service Selection */
          <div className="space-y-4">
            {/* Selected Country Banner */}
            <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <span className="text-3xl">{getCountryFlag(getCountryCode(selectedCountry))}</span>
              <div className="flex-1">
                <p className="font-bold text-gray-900">{selectedCountry}</p>
                <p className="text-xs text-indigo-600">{availableServices.length} services available</p>
              </div>
              <button
                onClick={() => setSelectedCountry(null)}
                className="p-2 hover:bg-indigo-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-indigo-600" />
              </button>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-2 gap-3">
              {availableServices.map((service) => {
                const Icon = getServiceIcon(service.name);
                const providerCount = allProviders.filter(
                  p => p.country === selectedCountry && p.service_uuid === service.service_uuid
                ).length;

                return (
                  <button
                    key={service.service_uuid}
                    onClick={() => setSelectedService(service)}
                    className="flex flex-col items-center gap-3 p-5 bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all"
                  >
                    <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-indigo-600" />
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-gray-900 text-sm">{service.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{providerCount} providers</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {availableServices.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p className="font-medium">No services available</p>
                <p className="text-sm mt-1">Try selecting a different country</p>
              </div>
            )}
          </div>
        ) : (
          /* Step 3: Provider Selection */
          <div className="space-y-4">
            {/* Service Banner */}
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
              <span className="text-2xl">{getCountryFlag(getCountryCode(selectedCountry))}</span>
              <div className="flex-1">
                <p className="font-bold text-gray-900">{selectedService.name}</p>
                <p className="text-xs text-gray-500">{selectedCountry}</p>
              </div>
              <button
                onClick={() => setSelectedService(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Provider Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search providers..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Provider Grid */}
            <div className="grid grid-cols-2 gap-3">
              {filteredProviders.map((provider) => (
                <button
                  key={provider.provider_uuid}
                  onClick={() => handleProviderSelect(provider)}
                  className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all"
                >
                  {provider.logo_url ? (
                    <img
                      src={provider.logo_url}
                      alt={provider.name}
                      className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-12 h-12 bg-indigo-100 rounded-lg items-center justify-center ${provider.logo_url ? 'hidden' : 'flex'}`}
                  >
                    <span className="text-indigo-600 font-bold text-lg">
                      {provider.name.charAt(0)}
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-900 text-sm line-clamp-2">{provider.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{provider.currency}</p>
                  </div>
                </button>
              ))}
            </div>

            {filteredProviders.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p className="font-medium">No providers found</p>
                {providerSearch && (
                  <p className="text-sm mt-1">Try a different search term</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PayBillSlider - using production API */}
      {showPayBillSlider && selectedProvider && (
        <PayBillSlider
          provider={selectedProvider}
          onClose={handleClosePayBillSlider}
          cashbackPercent={cashbackPercent || 0}
          useProductionApi={true}
        />
      )}
    </div>
  );
}
